import {useMutation} from '@apollo/client';
import {useCallback} from 'react';

import type {
    UpsertPurchaseMutation,
    UpsertPurchaseMutationVariables,
} from 'web-app/react/modals-container/purchase-modal/__generated__/queries.api-types';
import {UpsertPurchase} from 'web-app/react/modals-container/purchase-modal/queries';
import type {PurchasesQuery, PurchasesQueryVariables} from 'web-app/react/purchases/__generated__/queries.api-types';
import {defaultPurchaseOrdering} from 'web-app/react/purchases/constants';
import {Purchases} from 'web-app/react/purchases/queries';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import type {ApiAdhocPurchase} from '../api-types';

export const useCreateAdhocPurchases = () => {
    const [createAdhocPurchases, {loading: isAdhocPurchasesCreationLoading, client}] = useMutation<
        UpsertPurchaseMutation,
        UpsertPurchaseMutationVariables
    >(UpsertPurchase);

    const createAdhocPurchasesForChildren = useCallback(
        async ({adhocPurchases}: {adhocPurchases: Array<ApiAdhocPurchase>}) => {
            return createAdhocPurchases({
                variables: {
                    purchases: adhocPurchases,
                },
            }).then(result => {
                const cacheVariables = {
                    childIds: adhocPurchases.map(purchase => purchase.childId),
                    childId: adhocPurchases.length === 1 ? adhocPurchases[0].childId : null,
                    ordering: defaultPurchaseOrdering,
                    count: 10,
                };

                const currentData = client.readQuery<PurchasesQuery, PurchasesQueryVariables>({
                    query: Purchases,
                    variables: cacheVariables,
                });

                if (!currentData?.finance.purchases.filter.purchases) {
                    return;
                }

                const purchases = currentData.finance.purchases.filter.purchases;

                const newPurchase = result?.data?.finance.purchases.update?.[0];
                if (hasValue(newPurchase)) {
                    const updatedPurchases = purchases.concat(newPurchase);

                    client.writeQuery<PurchasesQuery, PurchasesQueryVariables>({
                        query: Purchases,
                        variables: cacheVariables,
                        data: {
                            ...currentData,
                            finance: {
                                ...currentData.finance,
                                purchases: {
                                    ...currentData.finance.purchases,
                                    filter: {
                                        ...currentData.finance.purchases.filter,
                                        purchases: updatedPurchases,
                                    },
                                },
                            },
                        },
                    });
                }
            });
        },
        [client, createAdhocPurchases],
    );
    return {createAdhocPurchasesForChildren, isLoading: isAdhocPurchasesCreationLoading};
};
